import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu, User, LogOut, LayoutDashboard } from "lucide-react";
import { useState } from "react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import MobileMenuDrawer from "@/components/shop/MobileMenuDrawer";

const nav = [
  { to: "/shop", label: "Shop" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function SiteHeader() {
  const { count, setDrawerOpen } = useCart();
  const { user, logout } = useAuth();
  const [drawer, setDrawer] = useState(false);
  const [userMenu, setUserMenu] = useState(false);

  return (
    <>
      <header data-testid="site-header" className="sticky top-0 z-40 backdrop-blur-md border-b bg-white/90 border-slate-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 h-24 md:h-28 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo">
            <img src="/products/ms_logo.png" alt="Morning Soft" className="h-20 md:h-28 w-auto object-contain" />
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-semibold rounded-full transition-colors ${
                    isActive ? "text-[#173a7a] bg-sky-50" : "text-slate-600 hover:text-[#173a7a]"
                  }`
                }>
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            {/* Desktop user menu */}
            <div className="hidden md:block relative">
              {user ? (
                <button onClick={() => setUserMenu((v) => !v)} data-testid="header-user-menu"
                  className="p-2.5 rounded-full hover:bg-slate-100">
                  <User size={16} />
                </button>
              ) : (
                <Link to="/login" data-testid="header-login-link"
                  className="px-4 py-2 rounded-full text-sm font-semibold text-slate-600 hover:text-[#173a7a]">
                  Sign in
                </Link>
              )}
              {userMenu && user && (
                <div className="absolute right-0 mt-2 w-56 rounded-2xl bg-white border border-slate-100 shadow-xl overflow-hidden" data-testid="user-dropdown">
                  <div className="px-4 py-3 border-b border-slate-100">
                    <p className="font-bold text-sm text-slate-900 truncate">{user.name || user.email}</p>
                    <p className="text-xs text-slate-500 truncate">{user.email}</p>
                  </div>
                  {user.role === "admin" && (
                    <Link to="/admin" onClick={() => setUserMenu(false)} data-testid="header-admin-link"
                      className="flex items-center gap-2 px-4 py-3 text-sm font-semibold hover:bg-slate-50">
                      <LayoutDashboard size={14} /> Admin Dashboard
                    </Link>
                  )}
                  <button onClick={() => { logout(); setUserMenu(false); }} data-testid="header-logout"
                    className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                    <LogOut size={14} /> Sign out
                  </button>
                </div>
              )}
            </div>

            {/* Cart (all screens) */}
            <button data-testid="header-cart-btn" onClick={() => setDrawerOpen(true)}
              className="relative flex items-center gap-2 px-3.5 md:px-4 py-2.5 rounded-full bg-[#173a7a] text-white text-sm font-semibold hover:bg-[#1a4694] transition-colors">
              <ShoppingBag size={16} />
              <span className="hidden sm:inline">Cart</span>
              {count > 0 && (
                <span data-testid="header-cart-count"
                  className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-amber-400 text-[#173a7a] text-[11px] font-extrabold flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>

            {/* Mobile hamburger */}
            <button data-testid="mobile-menu-toggle" onClick={() => setDrawer(true)}
              className="md:hidden p-2.5 rounded-full hover:bg-slate-100">
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>
      <MobileMenuDrawer open={drawer} onClose={() => setDrawer(false)} />
    </>
  );
}
